import { useMatchRoute, useNavigate } from '@tanstack/react-router'
import { XIcon } from 'lucide-react'
import { ReactNode } from 'react'

import { Route } from '@/routing/router'
import { ExternalLink, InternalLink } from '@/routing/types'
import { IconButton } from '@/ui/atoms/icon-button/IconButton'
import { cn } from '@/ui/utils/style'

export interface TopBannerProps {
  onClose: () => void
  className?: string
}

export type TopBannerContent =
  | {
      type: 'internal'
      linkProps: InternalLink
      pathToMatch: Route
      content: ReactNode
    }
  | {
      type: 'external'
      linkProps: ExternalLink
      content: ReactNode
    }

const topBannerContent: TopBannerContent = {
  type: 'internal',
  linkProps: InternalLink({
    to: '/savings/$domain/$savingsToken',
    params: {
      domain: 'mainnet',
      savingsToken: 'spusdc',
    },
  }),
  pathToMatch: '/savings/$domain/$savingsToken',
  content: 'Save with USDC, USDT, ETH and PYUSD in Spark Savings',
}

export const TOP_BANNER_ID = 'top-banner-21-10-2025'

export function TopBanner({ onClose, className }: TopBannerProps) {
  const matchRoute = useMatchRoute()
  const isMatch =
    topBannerContent.type === 'internal' ? !!matchRoute({ to: topBannerContent.pathToMatch, fuzzy: true }) : false
  const navigate = useNavigate()

  return (
    <>
      {/* Placeholder div to maintain space in document flow */}
      <div className="typography-body-3 invisible p-2.5">{topBannerContent.content}</div>

      {/* Fixed positioned banner */}
      <div
        className={cn(
          'relative flex w-full items-center justify-center bg-[#5723F2]',
          'min-h-10 p-2.5 text-center shadow-inner transition-colors duration-200 sm:flex-row md:p-1.5',
          (topBannerContent.type === 'external' || !isMatch) && 'cursor-pointer hover:bg-[#3a00a6]',
          'fixed top-0 right-0 left-0',
          className,
        )}
        onClick={() =>
          topBannerContent.type === 'internal'
            ? !isMatch && void navigate(topBannerContent.linkProps)
            : window.open(topBannerContent.linkProps.to, '_blank', 'noopener noreferrer')
        }
      >
        <div className="typography-body-3 text-primary-inverse">{topBannerContent.content}</div>
        <IconButton
          icon={XIcon}
          variant="transparent"
          size="s"
          onClick={(e) => {
            e.stopPropagation()
            onClose()
          }}
          className="-translate-y-1/2 absolute top-1/2 right-3 text-primary-inverse hover:text-primary-inverse/70 active:text-primary-inverse/50"
        />
      </div>
    </>
  )
}
